import styled from '@emotion/styled'
import { css } from '@emotion/react'
import useMediaQuery from '@mui/material/useMediaQuery'
import PropTypes from 'prop-types'

import AdviceUndoControl from '../AdviceUndoControl'
import FooterBar from '../FooterBar'
import { PrimaryButton, SecondaryButton } from '../Button'
import { MOBILE_VIEWPORT } from '../../guided-toolbox/utils/breakpoints'

// Keep the footer below StepScroll in the page layout. If the footer were fixed,
// StepScroll could scroll behind it and show around the desktop footer's edges.
//
// The desktop footer has no top margin because that margin would leave a gap
// between StepScroll and the footer.
const Bar = styled(FooterBar)`
  flex: 0 0 auto;
  padding: 15px 20px;
  margin: ${p => (p.isMobile ? '0' : '0 40px 20px')};
  box-shadow: ${p => (p.isMobile ? '0 1px 30px 7px rgba(122, 142, 150, 0.25)' : 'none')};
  background-color: ${p => p.theme.topNavColor};
`

// On mobile, remove this wrapper from the layout so Back and Next can share
// the footer width equally.
const Actions = styled.div`
  display: ${p => (p.isMobile ? 'contents' : 'flex')};
  align-items: center;
  column-gap: 35px;
`

/* Reduce the standard minimum width and font size so both buttons fit side by side at 375px. */
const navButton = css`
  min-width: 150px;
  font-size: 16px;
`

const BackButton = styled(SecondaryButton)`
  ${navButton};
`

const NextButton = styled(PrimaryButton)`
  ${navButton};
`

const AdviceFooter = props => {
  const {
    onBack,
    onNext,
    onUndo,
    backLabel = '< Back',
    nextLabel = 'Next >',
    nextLabelMobile,
    backDisabled = false,
    nextDisabled = false,
    showUndo = false,
    leftOfNext = null,
    className,
  } = props

  const isMobile = useMediaQuery(MOBILE_VIEWPORT)

  // Mobile renders undo in AdviceTopNav instead.
  const showFooterUndo = showUndo && !isMobile

  // Supplemental controls (e.g. the SSI "don't show me again" checkbox, the
  // summary's "walk me through" link) move into the step content on mobile.
  const showLeftOfNext = leftOfNext && !isMobile

  const primaryLabel = isMobile ? (nextLabelMobile ?? nextLabel) : nextLabel

  return (
    <Bar className={className} isMobile={isMobile}>
      <BackButton type='button' onClick={onBack} disabled={backDisabled}>
        {backLabel}
      </BackButton>

      <Actions isMobile={isMobile}>
        {showLeftOfNext && leftOfNext}
        {showFooterUndo && <AdviceUndoControl onClick={onUndo} />}
        <NextButton type='button' onClick={onNext} disabled={nextDisabled}>
          {primaryLabel}
        </NextButton>
      </Actions>
    </Bar>
  )
}

AdviceFooter.propTypes = {
  onBack: PropTypes.func,
  onNext: PropTypes.func,
  onUndo: PropTypes.func,
  backLabel: PropTypes.string,
  nextLabel: PropTypes.string,
  /** Mobile label; falls back to nextLabel. */
  nextLabelMobile: PropTypes.string,
  backDisabled: PropTypes.bool,
  nextDisabled: PropTypes.bool,
  showUndo: PropTypes.bool,
  /** Supplemental content shown left of the primary button on desktop. */
  leftOfNext: PropTypes.node,
  className: PropTypes.string,
}

export default AdviceFooter
